import {
  VulnerabilitySeverity,
  VulnerabilityStatus,
  vulnerabilitySeverityToKey,
  vulnerabilityStatusToKey,
  VULNERABILITY_SEVERITIES,
  VULNERABILITY_OPEN_STATUSES
} from '../../shared'
import { Application } from '../../types/application'
import { Grade } from '../../types/grade'
import { VulnerabilitySeverityBreakdown } from '../../types/vulnerability-severity-breakdown'
import { GradeValue } from '../../types/grade-value'
import { ApplicationImportance } from '../../types/application-importance'
import { transformRole } from './role'
import { BaseCompliancePolicy } from '../../types/compliance-policy'
import {
  MetadataValue,
  MetadataFieldType,
  MetadataSubfieldType
} from '../../types/metadata-value'
import { InvalidCustomFieldType } from '../../types/invalid-custom-field'
import { AgentLanguage } from '../../../../components/agent-language'
import { AgentLanguageKey } from '../../../../components/agent-language/agent-language'
import { Connector } from '../../../../api/connector'

type StatusDetails = Array<{
  readonly status: VulnerabilityStatus
  readonly traces: number
}>

interface SeverityBreakdown {
  readonly severity: VulnerabilitySeverity
  readonly traces: number
  readonly details: StatusDetails
}

const mergeArrayOfObjects = (acc, curr) => ({ ...acc, ...curr })

const emptyBreakdown = VULNERABILITY_SEVERITIES.map((severity) => ({
  [severity]: {
    total: 0,
    open: 0
  }
})).reduce(mergeArrayOfObjects, {})

function getOpenTotalFromStatusDetails(details: StatusDetails): number {
  return details.reduce((acc, { status, traces }) => {
    if (
      VULNERABILITY_OPEN_STATUSES.indexOf(vulnerabilityStatusToKey[status]) > -1
    ) {
      return acc + traces
    }
    return acc
  }, 0)
}

export function transformVulnerabilityBreakdown(
  vulnerabilityBreakdown: SeverityBreakdown[]
) {
  return {
    ...emptyBreakdown,
    ...vulnerabilityBreakdown
      .map(({ severity, traces, details }) => ({
        [vulnerabilitySeverityToKey[severity]]: {
          total: traces,
          open: getOpenTotalFromStatusDetails(details),
          ...details
            .map(({ status, traces }) => ({
              [vulnerabilityStatusToKey[status]]: traces
            }))
            .reduce(mergeArrayOfObjects, {})
        }
      }))
      .reduce(mergeArrayOfObjects, {}),
    total: vulnerabilityBreakdown
      .map(({ traces }) => traces)
      .reduce((acc, curr) => acc + curr, 0),
    open: vulnerabilityBreakdown
      .map(({ details }) => getOpenTotalFromStatusDetails(details))
      .reduce((acc, curr) => acc + curr, 0)
  }
}

interface Policy {
  readonly policy_id: number
  readonly name: string
  readonly enabled: boolean
}

interface MetadataEntity {
  readonly entityID: string
  readonly fieldName: string
  readonly fieldValue: string
  readonly unique: boolean
  readonly type: MetadataFieldType
  readonly subfields?: Array<{
    readonly entityID: string
    readonly fieldName: string
    readonly fieldValue: string
    readonly unique: boolean
    readonly type: MetadataSubfieldType
    readonly overriddenByUser: boolean
  }>
  readonly overriddenByUser: boolean
}

interface MetadataFieldEntity {
  readonly displayLabel: string
  readonly agentLabel: string
  readonly subfields?: Array<{
    readonly displayLabel: string
    readonly agentLabel: string
  }>
}

export function transformPolicies(policies?: Policy[]): BaseCompliancePolicy[] {
  return policies
    ? policies.map(({ policy_id, name, enabled }) => ({
        id: policy_id + '',
        name,
        enabled
      }))
    : []
}

function transformCustomMetadata(fields?: MetadataEntity[]): MetadataValue[] {
  return fields
    ? fields.map((field) => {
        const {
          fieldName,
          fieldValue,
          type,
          unique,
          subfields,
          overriddenByUser
        } = field
        return {
          name: fieldName,
          value: fieldValue,
          type,
          unique,
          subfields: subfields
            ? subfields.map(
                ({ fieldName, fieldValue, type, unique, overriddenByUser }) => {
                  return {
                    name: fieldName,
                    value: fieldValue,
                    unique,
                    type,
                    isOverriddenByUser: overriddenByUser
                  }
                }
              )
            : [],
          isOverriddenByUser: overriddenByUser
        }
      })
    : []
}

function getAgentLabel(field: MetadataFieldEntity, type: string) {
  // eslint-disable-next-line unicorn/prefer-ternary
  if (field.subfields && field.subfields.length) {
    return {
      id: field.subfields.map((field) => field.agentLabel).join(', '),
      validationType: type
    }
  } else {
    return { id: field.agentLabel, validationType: type }
  }
}

function transformInvalidFieldMetadata(
  validationErrorFields?: MetadataFieldEntity[],
  missingRequiredFields?: MetadataFieldEntity[]
): InvalidCustomFieldType[] {
  const invalidFields: InvalidCustomFieldType[] = validationErrorFields
    ? validationErrorFields.map((field) => getAgentLabel(field, 'invalid'))
    : []
  const missingFields: InvalidCustomFieldType[] = missingRequiredFields
    ? missingRequiredFields.map((field) => getAgentLabel(field, 'missing'))
    : []

  return invalidFields.concat(missingFields)
}

export const normalizeApplicationLanguage: (
  language: string
) => AgentLanguageKey = (language: string) => {
  return AgentLanguage.fromValue(language).getName()
}

export const normalizeApplication = ({
  app_id: id,
  name,
  language,
  trace_severity_breakdown,
  archived,
  tags,
  assess,
  defend,
  license,
  roles,
  attack_status,
  active_attacks,
  path,
  size,
  importance_description: importanceDescription,
  techs,
  status,
  last_seen: lastActivity,
  primary,
  total_modules: moduleCount,
  policies,
  metadataEntities,
  validationErrorFields,
  missingRequiredFields,
  scores
}) => ({
  id,
  name,
  language: normalizeApplicationLanguage(language),
  vulnerabilityBreakdown: transformVulnerabilityBreakdown(
    trace_severity_breakdown
  ) as VulnerabilitySeverityBreakdown,
  archived,
  tags,
  hasAssess: !!assess,
  hasAProtectedServer: !!defend,
  license:
    !license || license.level !== 'Licensed'
      ? null
      : {
          expiration: license.end
        },
  role: transformRole(roles),
  attackStatus: attack_status,
  attackTotal: !active_attacks ? 0 : active_attacks,
  url: path,
  linesOfCode: size,
  importance: importanceDescription as ApplicationImportance,
  technologies: techs,
  online: status === 'online',
  lastActivity,
  primary,
  moduleCount,
  violatedCompliancePolicies: transformPolicies(policies),
  customMetadataFields: transformCustomMetadata(metadataEntities),
  invalidCustomMetadataFields: transformInvalidFieldMetadata(
    validationErrorFields,
    missingRequiredFields
  ),
  scoreGrade: getScoreGrade(scores)
})

export async function findByIDAndOrganizationID(
  applicationID: string,
  organizationID: string,
  connector: Connector
): Promise<Application> {
  const { application } = await connector.get(
    `/${organizationID}/applications/${applicationID}?expand=license,compliance_policy,skip_links,technologies,trace_severity_breakdown,metadata`
  )

  return normalizeApplication(application)
}

export const getScoreGrade = (scores: { readonly letter_grade?: string }) => {
  return scores
    ? mapScoresLetterGrade(scores.letter_grade)
    : ('UNKNOWN' as GradeValue)
}

// Map '?' to a value that is valid for a GraphQLEnumType key
export const mapScoresLetterGrade = (letterGrade?: null | string) => {
  if (letterGrade === null || typeof letterGrade === 'undefined') {
    return 'UNKNOWN'
  }
  if (letterGrade === '?') {
    return 'UNKNOWN'
  }
  return letterGrade as GradeValue
}

export const mapScoresNumericalGrade = (grade?: null | number) =>
  grade !== null && typeof grade !== 'undefined' ? grade : 0

export const mapApplicationScoresData = (scores: {
  readonly grade?: null | number
  readonly letter_grade?: null | string
}) => {
  return {
    score: mapScoresNumericalGrade(scores.grade),
    value: mapScoresLetterGrade(scores.letter_grade)
  }
}

export async function findGradeByIDAndOrganizationID(
  applicationID: string,
  organizationID: string,
  connector: Connector
): Promise<Grade> {
  const { scores } = await connector.get(
    `/${organizationID}/applications/${applicationID}/score`
  )

  return mapApplicationScoresData(scores)
}
